import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Play, Clock, Video, Music, FileText, File, 
  GripVertical, ChevronRight, Eye, X, Upload, Link, Loader2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_TYPES = [
  { id: 'video', name: 'Video', icon: Video, description: 'YouTube or video file' },
  { id: 'audio', name: 'Audio', icon: Music, description: 'Audio file or podcast' },
  { id: 'text', name: 'Text', icon: FileText, description: 'Written content' },
  { id: 'document', name: 'Document', icon: File, description: 'PDF or document' }
];

const ProgramDetail = ({ user }) => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [contentSource, setContentSource] = useState('url'); // 'url' or 'upload'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [sessionForm, setSessionForm] = useState({
    title: '',
    description: '',
    content_type: 'video',
    content_url: '',
    content_text: '',
    duration_minutes: 10,
    order: 0
  });

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const canManageSessions = isAdmin || user?.role === 'educator';

  const fetchProgram = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(`${API_URL}/programs/${programId}`, { headers });
      setProgram(res.data);
      setSessions(res.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch program:', error);
      toast.error('Failed to load program');
      navigate('/dashboard/programs');
    } finally {
      setLoading(false);
    }
  }, [programId, navigate]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const newOrder = editingSession ? sessionForm.order : sessions.length;
      
      if (editingSession) {
        await axios.put(`${API_URL}/programs/${programId}/sessions/${editingSession.id}`, {
          ...sessionForm,
          order: newOrder
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Session updated');
      } else {
        await axios.post(`${API_URL}/programs/${programId}/sessions`, {
          ...sessionForm,
          order: newOrder
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Session created');
      }

      setShowSessionModal(false);
      resetSessionForm();
      fetchProgram();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save session');
    }
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    setSessionForm({
      title: session.title,
      description: session.description || '',
      content_type: session.content_type,
      content_url: session.content_url || '',
      content_text: session.content_text || '',
      duration_minutes: session.duration_minutes || 10,
      order: session.order || 0
    });
    setShowSessionModal(true);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/programs/${programId}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session deleted');
      fetchProgram();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = sessionForm.content_type;
    const maxSize = fileType === 'video' ? 500 * 1024 * 1024 : 50 * 1024 * 1024; // 500MB for video, 50MB for others
    
    if (file.size > maxSize) {
      toast.error(`File too large. Max size: ${fileType === 'video' ? '500MB' : '50MB'}`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);

      const response = await axios.post(`${API_URL}/sessions/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setSessionForm({ ...sessionForm, content_url: response.data.file_url });
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetSessionForm = () => {
    setEditingSession(null);
    setContentSource('url');
    setSessionForm({
      title: '',
      description: '',
      content_type: 'video',
      content_url: '',
      content_text: '',
      duration_minutes: 10,
      order: 0
    });
  };

  const openAddSessionModal = () => {
    resetSessionForm();
    setShowSessionModal(true);
  };

  const getContentIcon = (type) => {
    const contentType = CONTENT_TYPES.find(t => t.id === type);
    const IconComponent = contentType?.icon || File;
    return <IconComponent size={18} />;
  };

  const getPillarColor = () => {
    switch (program?.pillar) {
      case 'wellness': return '#10B981';
      case 'skill': return '#3B82F6';
      case 'recovery': return '#8B5CF6';
      default: return '#7C3AED';
    }
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="content-area" style={{ textAlign: 'center', padding: '60px' }}>
        <h2>Program not found</h2>
        <button onClick={() => navigate('/dashboard/programs')} className="gradient-btn" style={{ marginTop: '20px' }}>
          Back to Programs
        </button>
      </div>
    );
  }

  const pillarColor = getPillarColor();

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '16px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => navigate('/dashboard/programs')}
          style={{
            padding: '10px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--text-primary)'
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{
              padding: '4px 12px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              background: `${pillarColor}15`,
              color: pillarColor,
              textTransform: 'capitalize'
            }}>
              {program.pillar?.replace('_', ' ')} Enablement
            </span>
            <span style={{
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              background: program.status === 'published' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
              color: program.status === 'published' ? '#10B981' : '#F59E0B',
              textTransform: 'capitalize'
            }}>
              {program.status}
            </span>
          </div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
            {program.title}
          </h1>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
            {program.description}
          </p>
        </div>
        {canManageSessions && (
          <button
            onClick={openAddSessionModal}
            className="gradient-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
          >
            <Plus size={18} />
            Add Session
          </button>
        )}
      </div>

      {/* Program Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '16px', 
        marginBottom: '32px' 
      }}>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: pillarColor }}>
            {sessions.length}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Sessions</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: pillarColor }}>
            {program.duration_minutes || 0}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Total Minutes</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: pillarColor }}>
            {program.category?.name || '-'}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Category</div>
        </div>
      </div>

      {/* Sessions List */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Sessions ({sessions.length})
        </h2>

        {sessions.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px', 
            background: 'var(--bg-secondary)', 
            borderRadius: '12px',
            border: '1px dashed var(--border-color)'
          }}>
            <Play size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No Sessions Yet</h3>
            <p style={{ margin: '0 0 20px 0', color: 'var(--text-secondary)' }}>
              {canManageSessions ? 'Add your first session to get started' : 'Sessions will appear here once added'}
            </p>
            {canManageSessions && (
              <button
                onClick={openAddSessionModal}
                className="gradient-btn"
                style={{ padding: '12px 24px' }}
              >
                <Plus size={18} style={{ marginRight: '8px' }} />
                Add First Session
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {sessions.sort((a, b) => a.order - b.order).map((session, index) => (
              <div 
                key={session.id}
                className="card"
                style={{ 
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  transition: 'all 0.2s'
                }}
              >
                {/* Order Number */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: `${pillarColor}15`,
                  color: pillarColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '14px',
                  flexShrink: 0
                }}>
                  {index + 1}
                </div>

                {/* Content Type Icon */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-tertiary)',
                  flexShrink: 0
                }}>
                  {getContentIcon(session.content_type)}
                </div>

                {/* Session Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ 
                    margin: '0 0 4px 0', 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {session.title}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ 
                      fontSize: '12px', 
                      color: 'var(--text-tertiary)', 
                      textTransform: 'capitalize' 
                    }}>
                      {session.content_type}
                    </span>
                    <span style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      fontSize: '12px', 
                      color: 'var(--text-tertiary)' 
                    }}>
                      <Clock size={12} />
                      {session.duration_minutes} min
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={() => navigate(`/dashboard/programs/${programId}/sessions/${session.id}`)}
                    style={{
                      padding: '8px 16px',
                      background: pillarColor,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    <Play size={14} />
                    Start
                  </button>
                  {canManageSessions && (
                    <>
                      <button
                        onClick={() => handleEditSession(session)}
                        style={{
                          padding: '8px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)'
                        }}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        style={{
                          padding: '8px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: '#EF4444'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Session Modal */}
      {showSessionModal && (
        <div className="modal-overlay" onClick={() => setShowSessionModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {editingSession ? 'Edit Session' : 'Add Session'}
              </h2>
              <button
                onClick={() => setShowSessionModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-secondary)' }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateSession}>
              {/* Title */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  Session Title *
                </label>
                <input
                  type="text"
                  value={sessionForm.title}
                  onChange={e => setSessionForm({ ...sessionForm, title: e.target.value })}
                  required
                  placeholder="e.g., Introduction to Meditation"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  Description
                </label>
                <textarea
                  value={sessionForm.description}
                  onChange={e => setSessionForm({ ...sessionForm, description: e.target.value })}
                  rows={3}
                  placeholder="Brief description of this session..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Content Type Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  Content Type *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                  {CONTENT_TYPES.map(type => {
                    const IconComponent = type.icon;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setSessionForm({ ...sessionForm, content_type: type.id })}
                        style={{
                          padding: '12px 8px',
                          border: sessionForm.content_type === type.id ? `2px solid ${pillarColor}` : '1px solid var(--border-color)',
                          borderRadius: '8px',
                          background: sessionForm.content_type === type.id ? `${pillarColor}10` : 'var(--bg-secondary)',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                      >
                        <IconComponent size={20} style={{ color: sessionForm.content_type === type.id ? pillarColor : 'var(--text-tertiary)' }} />
                        <span style={{ fontSize: '12px', fontWeight: '500', color: sessionForm.content_type === type.id ? pillarColor : 'var(--text-secondary)' }}>
                          {type.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content Source Toggle (for video, audio, document) */}
              {sessionForm.content_type !== 'text' && (
                <div style={{ marginBottom: '16px' }}>
                  {/* Toggle: Upload or URL */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setContentSource('upload')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '8px',
                        border: contentSource === 'upload' ? `2px solid ${pillarColor}` : '1px solid var(--border-color)',
                        background: contentSource === 'upload' ? `${pillarColor}10` : 'var(--bg-secondary)',
                        color: contentSource === 'upload' ? pillarColor : 'var(--text-secondary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      <Upload size={16} />
                      Upload File
                    </button>
                    <button
                      type="button"
                      onClick={() => setContentSource('url')}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '8px',
                        border: contentSource === 'url' ? `2px solid ${pillarColor}` : '1px solid var(--border-color)',
                        background: contentSource === 'url' ? `${pillarColor}10` : 'var(--bg-secondary)',
                        color: contentSource === 'url' ? pillarColor : 'var(--text-secondary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      <Link size={16} />
                      Enter URL
                    </button>
                  </div>

                  {/* Upload Section */}
                  {contentSource === 'upload' && (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={
                          sessionForm.content_type === 'video' ? 'video/mp4,video/webm,video/quicktime' :
                          sessionForm.content_type === 'audio' ? 'audio/mpeg,audio/wav,audio/ogg' :
                          'application/pdf,.doc,.docx,.ppt,.pptx'
                        }
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                      <div 
                        onClick={() => !uploading && fileInputRef.current?.click()}
                        style={{
                          padding: '32px',
                          border: '2px dashed var(--border-color)',
                          borderRadius: '8px',
                          textAlign: 'center',
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          background: 'var(--bg-secondary)',
                          transition: 'all 0.2s'
                        }}
                      >
                        {uploading ? (
                          <>
                            <Loader2 size={32} style={{ color: pillarColor, marginBottom: '12px', animation: 'spin 1s linear infinite' }} />
                            <p style={{ margin: 0, fontWeight: '600', color: 'var(--text-primary)' }}>
                              Uploading... {uploadProgress}%
                            </p>
                            <div style={{ 
                              width: '100%', 
                              height: '4px', 
                              background: 'var(--border-color)', 
                              borderRadius: '2px', 
                              marginTop: '12px',
                              overflow: 'hidden'
                            }}>
                              <div style={{ 
                                width: `${uploadProgress}%`, 
                                height: '100%', 
                                background: pillarColor,
                                transition: 'width 0.3s'
                              }} />
                            </div>
                          </>
                        ) : (
                          <>
                            <Upload size={32} style={{ color: 'var(--text-tertiary)', marginBottom: '12px' }} />
                            <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: 'var(--text-primary)' }}>
                              Click to upload or drag & drop
                            </p>
                            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-tertiary)' }}>
                              {sessionForm.content_type === 'video' && 'MP4, WebM, MOV (max 500MB)'}
                              {sessionForm.content_type === 'audio' && 'MP3, WAV, OGG (max 50MB)'}
                              {sessionForm.content_type === 'document' && 'PDF, DOC, DOCX, PPT, PPTX (max 50MB)'}
                            </p>
                          </>
                        )}
                      </div>
                      {sessionForm.content_url && (
                        <div style={{ 
                          marginTop: '12px', 
                          padding: '12px', 
                          background: 'rgba(16, 185, 129, 0.1)', 
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            background: '#10B981' 
                          }} />
                          <span style={{ fontSize: '13px', color: '#10B981', fontWeight: '500' }}>
                            File uploaded successfully
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* URL Input Section */}
                  {contentSource === 'url' && (
                    <div>
                      <input
                        type="url"
                        value={sessionForm.content_url}
                        onChange={e => setSessionForm({ ...sessionForm, content_url: e.target.value })}
                        placeholder={
                          sessionForm.content_type === 'video' ? 'Paste YouTube, Vimeo, or direct video URL...' :
                          sessionForm.content_type === 'audio' ? 'Paste audio URL (MP3, WAV, SoundCloud)...' :
                          'Paste document URL (PDF, Google Docs, etc.)...'
                        }
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}
                      />
                      <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                        {sessionForm.content_type === 'video' && '✓ YouTube, Vimeo, or any direct video URL (.mp4, .webm)'}
                        {sessionForm.content_type === 'audio' && '✓ MP3, WAV, OGG, or streaming audio URLs'}
                        {sessionForm.content_type === 'document' && '✓ PDF, Google Docs, Dropbox, or any document URL'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Content Text (for text type) */}
              {sessionForm.content_type === 'text' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                    Content Text *
                  </label>
                  <textarea
                    value={sessionForm.content_text}
                    onChange={e => setSessionForm({ ...sessionForm, content_text: e.target.value })}
                    rows={8}
                    placeholder="Enter the text content for this session..."
                    required={sessionForm.content_type === 'text'}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: '1.6'
                    }}
                  />
                </div>
              )}

              {/* Duration */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  Estimated Duration (minutes) *
                </label>
                <input
                  type="number"
                  value={sessionForm.duration_minutes}
                  onChange={e => setSessionForm({ ...sessionForm, duration_minutes: parseInt(e.target.value) || 0 })}
                  min={1}
                  required
                  style={{
                    width: '120px',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowSessionModal(false)}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-btn"
                  style={{ padding: '12px 24px' }}
                >
                  {editingSession ? 'Update Session' : 'Create Session'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramDetail;
